import * as React from "react";
import axios from "axios";
import {
  FileText,
  FileSpreadsheet,
  Bot,
  Loader2,
  Download,
  Copy,
  Check,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from "./dialog";
import { Button } from "./button";
import { Label, Text, Caption } from "./typography";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { cn } from "../../lib/utils";
import { API_BASE_URL } from '../../lib/api';

/**
 * ExportDialog - Dialog for exporting portfolio data in various formats
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 */
export function ExportDialog({ open, onOpenChange }) {
  const [exportFormat, setExportFormat] = React.useState("ai-json");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [exportResult, setExportResult] = React.useState(null);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setError(null);
      setExportResult(null);
      setCopied(false);
    }
  }, [open]);

  const formatOptions = [
    {
      value: "ai-json",
      label: "AI Analysis (JSON)",
      description: "Structured data optimized for ChatGPT, Claude, and other AI tools",
      icon: Bot,
      recommended: true,
    },
    {
      value: "ai-markdown",
      label: "AI Analysis (Markdown)",
      description: "Readable format with tables, perfect for copying to AI chat",
      icon: Bot,
    },
    {
      value: "pdf",
      label: "PDF Report",
      description: "Professional portfolio summary with charts and tables",
      icon: FileText,
    },
    {
      value: "excel",
      label: "Excel Spreadsheet",
      description: "Multi-sheet workbook with formulas for detailed analysis",
      icon: FileSpreadsheet,
    },
  ];

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    setExportResult(null);
    setCopied(false);

    try {
      // Fetch CSRF token first to ensure it's valid
      const csrfResponse = await axios.get(`${API_BASE_URL}/csrf-token`, {
        withCredentials: true
      });
      const csrfToken = csrfResponse.data.csrfToken;

      let endpoint;
      let responseType;
      let format;

      if (exportFormat === "ai-json") {
        endpoint = "/exports/ai";
        responseType = "json";
        format = "json";
      } else if (exportFormat === "ai-markdown") {
        endpoint = "/exports/ai";
        responseType = "text";
        format = "markdown";
      } else if (exportFormat === "pdf") {
        endpoint = "/exports/pdf";
        responseType = "blob";
      } else if (exportFormat === "excel") {
        endpoint = "/exports/excel";
        responseType = "blob";
      }

      const response = await axios.post(
        `${API_BASE_URL}${endpoint}`,
        { format },
        {
          headers: {
            "X-CSRF-Token": csrfToken
          },
          withCredentials: true,
          responseType,
        }
      );

      // Handle different response types
      if (exportFormat === "ai-json" || exportFormat === "ai-markdown") {
        // For AI formats, show preview and copy option
        const data = exportFormat === "ai-json"
          ? JSON.stringify(response.data, null, 2)
          : response.data;
        setExportResult({
          type: exportFormat,
          data,
        });
      } else {
        // For PDF/Excel, trigger download
        const blob = new Blob([response.data], {
          type: exportFormat === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `portfolio-${new Date().toISOString().split("T")[0]}.${exportFormat === "pdf" ? "pdf" : "xlsx"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Close dialog after successful download
        onOpenChange(false);
      }
    } catch (err) {
      console.error("Export failed:", err);

      let errorMsg = "Failed to generate export";
      if (err.response?.status === 429) {
        errorMsg = "Too many export requests. Please wait a minute and try again.";
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!exportResult?.data) return;

    try {
      await navigator.clipboard.writeText(exportResult.data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      setError("Failed to copy to clipboard");
    }
  };

  const handleDownload = () => {
    if (!exportResult?.data) return;

    const blob = new Blob([exportResult.data], {
      type: exportFormat === "ai-json" ? "application/json" : "text/markdown"
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `portfolio-analysis-${new Date().toISOString().split("T")[0]}.${exportFormat === "ai-json" ? "json" : "md"}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="pb-2">
          <DialogTitle>Export Portfolio</DialogTitle>
          <DialogDescription>
            Export your portfolio data for analysis, reporting, or record-keeping
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="py-4">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3 mb-4">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* Export Result Preview */}
          {exportResult && (
            <div className="mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label>Export Ready</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyToClipboard}
                    disabled={copied}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
              <div className="rounded-md bg-muted p-3 max-h-64 overflow-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                  {exportResult.data.length > 3000
                    ? `${exportResult.data.slice(0, 3000)}...\n\n[Truncated for preview - full content copied/downloaded]`
                    : exportResult.data}
                </pre>
              </div>
              <Caption className="text-muted-foreground">
                Paste this into ChatGPT, Claude, or any AI assistant for portfolio analysis
              </Caption>
            </div>
          )}

          {/* Format Selection */}
          {!exportResult && (
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground mb-3">
                Export Format
              </legend>
              <RadioGroup
                value={exportFormat}
                onValueChange={setExportFormat}
                className="space-y-2"
              >
                {formatOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <label
                      key={option.value}
                      htmlFor={option.value}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                        exportFormat === option.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-medium text-sm">
                            {option.label}
                          </span>
                          {option.recommended && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {option.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </fieldset>
          )}
        </DialogBody>

        <DialogFooter>
          {exportResult ? (
            <Button
              variant="outline"
              onClick={() => {
                setExportResult(null);
                setExportFormat("ai-json");
              }}
            >
              Export Another Format
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button onClick={handleExport} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExportDialog;
